import { FormEvent } from 'react'
import styled from 'styled-components'
import { useFormikContext } from 'formik'

import { Grid, H, InfoCircleIcon, P, Wrapper } from '@farewill/ui'
import { COLOR, GTR } from '@farewill/ui/tokens'

import { BRAND_ID, MAP_BRAND_ID_TO_NAME } from 'config/brands'
import { FuneralPlanLeadAttributes } from 'lib/models/lead'
import { LIMC_CONSENT_VALUE } from 'lib/models/legitimate-interest-marketing-consent'
import AddressInputWrapper from 'components/address'
import { Checkbox, SelectInput } from 'components/form'
import ContactEmailInput from 'components/form/contact-email-input'
import Input from 'components/form/input'
import { Selected } from 'components/form/select-input/types'
import { MergeWarningForLeadPage as MergeWarning } from 'components/merge-warning'
import Tooltip from 'components/tooltip'
import { useOptimisticState } from 'hooks/useOptimisticState'
import {
  PRODUCTS,
  FUNERAL_PLAN_LEAD_HEARD_ABOUT_US_VIA_OPTIONS,
  LEAD_SOURCE_IDENTIFIER_OPTIONS,
  LEAD_SOURCE_TYPE_OPTIONS,
} from 'utils/enums'
import { getYearsElapsed, strToBool } from 'utils/helpers'

import { useLeadContext } from '../context'
import PartnersAndDiscounts from '../partners-and-discounts'

const StyledScriptContent = styled(Wrapper)`
  padding-left: ${GTR.S};
  border-left: 2px solid ${COLOR.GREY.LIGHT};
  font-style: italic;
`

const ContactDetails = (): React.ReactElement => {
  const { lead, fetchLead, updateContact, updateContactMeta } = useLeadContext()
  const { values, handleSubmit, setFieldValue } =
    useFormikContext<FuneralPlanLeadAttributes>()

  const serverMarketingConsentOptedIn =
    values.contact.marketingConsents?.general?.email ===
    LIMC_CONSENT_VALUE.subscribed

  // Optimistic state management to make checkbox feel responsive.
  const {
    currentValue: isMarketingConsentOptedInRaw,
    updateOptimistically: updateMarketingConsentOptimistically,
  } = useOptimisticState(serverMarketingConsentOptedIn)

  const isMarketingConsentOptedIn = isMarketingConsentOptedInRaw ?? undefined

  const handleMarketingConsentSave = async ({
    name,
    value,
  }: {
    name: string
    value: string
  }) => {
    const isOptedIn = value === 'true'

    await updateMarketingConsentOptimistically(isOptedIn, async () => {
      const consentValue = isOptedIn
        ? LIMC_CONSENT_VALUE.subscribed
        : LIMC_CONSENT_VALUE.unsubscribed
      const marketingConsents = {
        general: {
          email: consentValue,
          phoneNumber: consentValue,
          postal: consentValue,
        },
      }

      const updatedLeadResponse = await updateContact({
        name: 'contact.marketingConsents',
        value: marketingConsents,
      })

      // Update Formik state with the updated contact data from server
      const updatedContact = updatedLeadResponse.data.attributes.contact
      setFieldValue('contact', updatedContact)
    })
  }

  const sourceIdentifierOptions =
    LEAD_SOURCE_IDENTIFIER_OPTIONS[
      values.sourceType as keyof typeof LEAD_SOURCE_IDENTIFIER_OPTIONS
    ]

  const resetSourceIdentifierAndSubmit = (value: {
    name: string
    value: Selected
  }) => {
    setFieldValue('sourceIdentifier', null)
    handleSubmit(value as unknown as FormEvent<HTMLFormElement>)
  }

  const brandId = values.contact.brandId

  if (brandId === BRAND_ID.dignity) {
    return <>Invalid brand for funeral plan lead. Contact engineering. </>
  }

  return (
    <>
      <H size="S">The caller</H>
      <Grid style={{ gridAutoFlow: 'row' }}>
        <Grid.Item spanFromL={6}>
          <Input
            label="Preferred name (optional)"
            hint="We’ll use this in conversation, and at the start of emails and letters (e.g Hello Mr Smith, or Hello Paul)."
            name="contact.preferredName"
            handleSave={updateContact}
          />
        </Grid.Item>
        <Grid.Item spanFromL={2} startColumnFromL={1}>
          <Input
            label="Title"
            name="contact.title"
            handleSave={updateContact}
          />
        </Grid.Item>
        <Grid.Item spanFromL={5} startColumnFromL={3}>
          <Input
            label="First name"
            name="contact.firstName"
            handleSave={updateContact}
            allowCopyToClipboard
          />
        </Grid.Item>
        <Grid.Item spanFromL={5}>
          <Input
            label="Last name"
            name="contact.lastName"
            handleSave={updateContact}
            allowCopyToClipboard
          />
        </Grid.Item>
        <Grid.Item spanFromL={6}>
          <Input
            label="Date of birth"
            name="contact.dateOfBirth"
            type="date"
            handleSave={updateContact}
            hint={
              values.contact?.dateOfBirth
                ? `Age: ${getYearsElapsed({
                    dateOfBirth: values.contact.dateOfBirth,
                  })}`
                : undefined
            }
          />
        </Grid.Item>
        <Grid.Item spanFromL={6} startColumnFromL={1}>
          <Input
            label="Phone number"
            name="contact.phoneNumber"
            handleSave={updateContact}
            allowCopyToClipboard
          />
        </Grid.Item>
        <Grid.Item spanFromL={6} data-scroll-id="contact-email">
          <ContactEmailInput
            contactId={values.contact.id}
            handleSave={async ({ name, value }) => {
              await updateContact({ name, value })
            }}
            hasRelatedAccount={!!values.contact.accountUuid}
            inputName="contact.email"
            allowCopyToClipboard
          />
          <MergeWarning apiMessages={updateContactMeta?.messages} />
        </Grid.Item>
        <Grid.Item spanFromL={6}>
          <Checkbox
            label={
              <>
                <span>Customer requested postal service</span>
                <Tooltip content="Check this if the customer does not have an email address and wants all communication to be sent via post">
                  <Wrapper
                    padding={[0, 0, 0, 'XS']}
                    as={InfoCircleIcon}
                    size="S"
                  />
                </Tooltip>
              </>
            }
            name="quoteAttributes.isPostalService"
            checked={strToBool(values.quoteAttributes.isPostalService)}
            handleSave={() => handleSubmit()}
          />
        </Grid.Item>
        <Grid.Item>
          <AddressInputWrapper
            data={values.contact.addresses[0]}
            label="Address"
            name="contact.addresses"
            newAddressAttrs={{
              relatedResource: 'contact',
              relatedId: values.contactId,
              relation: 'addresses',
            }}
            warning={lead.attributes.addressValidationWarning}
            onSave={async () => {
              /**
               * We need to fetch the lead again to get the updated contact
               * with the new address as we rely on the address to allow the user
               * to send a brochure to the customer.
               */
              const { data } = await fetchLead()
              setFieldValue('contact', data.attributes.contact)
            }}
          />
        </Grid.Item>

        <Grid.Item>
          <H size="XS">Marketing consent</H>
          <P>
            Ask the user if they consent to receiving marketing communications
            from us.
          </P>

          {brandId === BRAND_ID.simplicity && (
            <StyledScriptContent
              margin={['M', 0, 'M']}
              padding={[0, 0, 0, 'S']}
              tag="blockquote"
            >
              <P>
                We would also like to tell you about our other similar offers,
                products and services which may be of interest to you, from
                Simplicity. Is this OK or would you prefer to opt out? You can
                change your mind and withdraw consent to marketing at any time,
                by either: emailing unsubscribe@simplicity.co.uk, phoning 0808
                2589755, or writing to us.
              </P>
            </StyledScriptContent>
          )}

          <Checkbox
            label="Opted in to marketing"
            name="marketingConsent"
            checked={isMarketingConsentOptedIn}
            handleSave={handleMarketingConsentSave}
          />
        </Grid.Item>

        <Grid.Item spanFromL={6}>
          <SelectInput
            label="Lead Type"
            name="sourceType"
            handleSave={resetSourceIdentifierAndSubmit}
            options={LEAD_SOURCE_TYPE_OPTIONS}
          />
        </Grid.Item>
        {sourceIdentifierOptions && (
          <Grid.Item spanFromL={6}>
            <SelectInput
              label="Source"
              name="sourceIdentifier"
              options={sourceIdentifierOptions}
              handleSave={() => handleSubmit()}
            />
          </Grid.Item>
        )}
        <Grid.Item>
          <PartnersAndDiscounts product={PRODUCTS.FUNERAL_PLAN} />
        </Grid.Item>
        <Grid.Item spanFromL={6}>
          <SelectInput
            label={`Where did they hear about ${MAP_BRAND_ID_TO_NAME[brandId]}?`}
            name="quoteAttributes.heardAboutFarewillVia"
            options={FUNERAL_PLAN_LEAD_HEARD_ABOUT_US_VIA_OPTIONS[brandId]}
            handleSave={() => handleSubmit()}
          />
        </Grid.Item>
      </Grid>
    </>
  )
}

export default ContactDetails
